import { computed, ref } from 'vue'
import {
  currentCaseIdsFor,
  effectiveCompanyOrder as effectiveOrderOf,
  mergeCaseUnread,
  nextNavQuery,
  normalizeNavPayload,
  pickLeadAgencyId,
  resolveNavCompany,
  resolveNavLeadAgency,
  visibleNavCompanies,
} from '~/utils/caseNavigation'
import { useApolloClient } from '@vue/apollo-composable'
import { CASE_NAV_DATA_QUERY } from '~/graphql/cases/queries'
import { readViewAsUserId, usePlanningImportScope } from '~/composables/usePlanningImportScope'
import { useUserPreferences } from '@/composables/useUserPreferences'
import { useFollowupsViewMemory } from '@/composables/useFollowupsViewMemory'
import { applyUserOrder } from '~/utils/displayOrder'
import { navTrace, navTraceFail } from '~/utils/navTrace'

/**
 * Single source of truth for the cases/followups navigation:
 *
 *   companies → lead agencies → case selection
 *
 * Replaces the split state that lived in `companiesStore` + `casesStore`
 * (selectedCompany, selectedLeadAgency, selectedCase) which races against
 * itself when the deep watchers in `pages/cases/followups.vue` fire on
 * unrelated mutations.
 *
 * Module-level singleton — every `useCaseNavigation()` call returns the
 * same shared state. That way the navbar component, the page, the case
 * list, the follow-ups list and (later) the header notifications all
 * observe the same selection without prop drilling or store coupling.
 *
 * What this composable owns:
 *   - `companies` (the result of getCaseNavData) + computed `visibleCompanies`
 *     (strictly those with in-scope cases, plus the selected one).
 *   - `selectedCompanyId` / `selectedLeadAgencyId` / `selectedCaseId`.
 *     IDs only — full case objects live in the page-level `useFollowupsPage`
 *     composable (to be added in Phase 3) since they're heavy and only
 *     needed in that page's context.
 *   - Per-case unread overrides (`caseUnread(id)`) so the case list badges
 *     can refresh from the same poll that updates the nav counts.
 *   - URL sync (`cid`, `aid`, `caid`) — single-direction `replace` so the
 *     URL mirrors selection; the previous bidirectional watcher caused
 *     loops (the URL update re-triggered the route watcher, which
 *     re-resolved the selection).
 *   - Per-context (company, lead agency) memory of the last selected case
 *     in `localStorage`. When the user changes context and comes back,
 *     the case they had selected before is restored.
 *
 * What this composable does NOT own (Phase 3+):
 *   - `activeCases` array, `selectedCase` object, plan-day + filters,
 *     `fetchActiveCasesForCompany`. Those move to `useFollowupsPage`.
 *   - Polling / visibility refresh / event invalidations (Phase 4).
 */

const _companies = ref([])

// The EFFECTIVE (viewed) user's saved company order, straight from the nav
// payload. Used INSTEAD of the current user's order while viewing as someone,
// so the bar shows their arrangement.
const _viewedCompanyOrder = ref([])
const _loading = ref(false)
const _hasInitialized = ref(false)

const _selectedCompanyId = ref(null)
const _selectedLeadAgencyId = ref(null)

// The user explicitly picked "All lead agencies" (LA = null on purpose, show the
// whole company). Distinguishes it from "not resolved yet" so `reconcileFromUrl`
// doesn't fall back to the first LA and bounce the selection back.
const _leadAgencyIsAll = ref(false)
const _selectedCaseId = ref(null)

const _caseUnreadById = ref(new Map())

// Unified view memory. This composable owns the company + lead-agency levels;
// the planning day, the per-day filters and the selected case are written from
// the store and the page (they need the resolved day to key correctly).
const viewMemory = useFollowupsViewMemory()

// Polling state lives at module scope so the timer is shared across
// every consumer of the composable. The Apollo client is cached on
// first access so `invalidateCaseNav` and the polling tick can refetch
// from a Pinia action / event handler / anywhere outside setup().
let _pollTimer = null
let _isPolling = false
let _visibilityHandler = null
let _currentCaseIdsGetter = null
let _resolveClient = null
let _navDataQuery = null

// Stage categories that count toward visibility. Followups uses ['active'];
// a future won/lost page sets e.g. ['won', 'lost'] via `startCaseNavPolling`.
let _categories = ['active']
const POLL_INTERVAL_MS = 25_000

/**
 * Core fetch — used by `useCaseNavigation().fetchNavData()`, the
 * polling tick, and `invalidateCaseNav()`. Lives at module scope so it
 * doesn't need `useRoute`/`useRouter`, which means callers from a
 * Pinia action or any non-setup context can drive a refresh.
 *
 * Reads the current "case ids to refresh per-case unread for" via the
 * registered getter; the page sets it during `start()` so polling
 * naturally hits the cases the user is looking at.
 */
async function _coreFetch(overrideIds) {
  if (!_resolveClient || !_navDataQuery) return

  _loading.value = true
  try {
    const { data } = await _resolveClient().query({
      query: _navDataQuery,
      variables: {
        currentCaseIds: currentCaseIdsFor(overrideIds, _currentCaseIdsGetter),
        caseStageCategories: _categories,

        // "View As": scope the nav (companies + lead agencies + unread) as the
        // viewed user, when set. Null = the caller's own nav (no change).
        viewAsUserId: readViewAsUserId(),
      },
      fetchPolicy: 'network-only',
    })

    // Companies AND their nested lead agencies ordered by `displayOrder` once,
    // here — every consumer downstream reads the ordered data
    // (utils/caseNavigation).
    const payload = normalizeNavPayload(data?.getCaseNavData)

    _viewedCompanyOrder.value = payload.companyOrder
    _companies.value = payload.companies
    _caseUnreadById.value = mergeCaseUnread(_caseUnreadById.value, payload.cases)
  } catch (err) {
    console.error('[useCaseNavigation] fetch failed', err)
  } finally {
    _loading.value = false
  }
}

function _scheduleNext() {
  if (_pollTimer) {
    clearTimeout(_pollTimer)
    _pollTimer = null
  }
  if (!_isPolling) return
  _pollTimer = setTimeout(async () => {
    if (typeof document !== 'undefined' && document.hidden) {
      // Skip the round-trip while hidden; the visibility handler will
      // catch up the moment the user comes back.
      _scheduleNext()

      return
    }
    await _coreFetch()
    _scheduleNext()
  }, POLL_INTERVAL_MS)
}

/**
 * Start the 25s polling loop + visibility refresh. Idempotent — calling
 * twice has no effect (the second call just updates the case ids
 * getter). Pass `currentCaseIds` as a function so the polling tick
 * always reads the latest set without needing re-registration.
 */
export function startCaseNavPolling({ currentCaseIds, caseStageCategories } = {}) {
  if (typeof currentCaseIds === 'function') {
    _currentCaseIdsGetter = currentCaseIds
  }

  // Which stage categories this page wants in the bar (default ['active']).
  if (Array.isArray(caseStageCategories) && caseStageCategories.length) {
    _categories = caseStageCategories.map(String)
  }
  if (_isPolling) return
  _isPolling = true
  if (typeof document !== 'undefined') {
    _visibilityHandler = () => {
      if (!document.hidden) {
        _coreFetch().catch(() => {})
      }
    }
    document.addEventListener('visibilitychange', _visibilityHandler)
  }
  _scheduleNext()
}

export function stopCaseNavPolling() {
  _isPolling = false
  if (_pollTimer) {
    clearTimeout(_pollTimer)
    _pollTimer = null
  }
  if (_visibilityHandler && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', _visibilityHandler)
    _visibilityHandler = null
  }
  _currentCaseIdsGetter = null
}

/**
 * Full module-level reset — invoked by `authStore.logout` so the
 * cached companies / selection / case-unread map from the previous
 * session don't leak into the next login.
 */
export function resetCaseNavState() {
  stopCaseNavPolling()
  _companies.value = []
  _viewedCompanyOrder.value = []
  _hasInitialized.value = false
  _selectedCompanyId.value = null
  _selectedLeadAgencyId.value = null
  _selectedCaseId.value = null
  _caseUnreadById.value = new Map()
}

/**
 * Force an immediate refetch + reset the polling interval. Wire this to
 * any mutation that affects unread counts or the active-cases set so
 * the user sees the new numbers without waiting up to 25s for the
 * next tick. Safe to call from Pinia actions or anywhere — does NOT
 * touch `useRoute`/`useRouter`.
 */
export async function invalidateCaseNav(_reason) {
  await _coreFetch()
  if (_isPolling) _scheduleNext()
}

export function useCaseNavigation() {
  const { resolveClient } = useApolloClient()
  const route = useRoute()
  const router = useRouter()
  const prefs = useUserPreferences()

  // Cache the apollo client + query at module scope on first invocation.
  // After this the module-level `_coreFetch` (used by polling and
  // `invalidateCaseNav`) can run without depending on a setup context.
  if (!_resolveClient) _resolveClient = resolveClient
  if (!_navDataQuery) _navDataQuery = CASE_NAV_DATA_QUERY

  // The user's custom company order (array of ids); empty → fall back to each
  // company's global `displayOrder`. Applied in `visibleCompanies` so the bar
  // AND the quick-switcher follow it.
  const companyOrder = prefs.orderFor('Company')

  // While viewing as someone, follow THEIR saved order (returned by the nav
  // payload) instead of mine — so the bar mirrors their arrangement. My own
  // order is restored automatically when view-as clears.
  const { viewAsUserId: activeViewAsUserId } = usePlanningImportScope()

  const effectiveCompanyOrder = computed(() =>
    effectiveOrderOf(activeViewAsUserId.value, _viewedCompanyOrder.value, companyOrder.value),
  )

  // True once `initFromUrl` has finished refreshing the nav payload and the
  // user preferences (the company order). The navbar uses this to decide
  // whether to render its chips or a skeleton.
  const ready = computed(() => _hasInitialized.value)

  /**
   * Companies shown in the bar: strictly those with at least one case in the
   * requested categories (`casesCount` from the backend), PLUS the currently-
   * selected one (so the active chip never vanishes when its last case leaves
   * the scope). No "show empty" escape hatch, no fallback-to-all.
   *
   * Each surviving company also has its lead agencies pruned to those with cases
   * (`casesQuantity > 0`), plus the currently-selected LA (same "active pill
   * never vanishes" reason). Both counts are ACTIVE + non-removed (the backend
   * `getCaseNavData` only tallies those). The selection logic below still reads
   * the RAW `_companies` (every LA), so `pickLeadAgencyId` keeps working.
   */
  const visibleCompanies = computed(() => {
    if (_companies.value.length === 0) return []

    // Per-user order (the viewed user's under view-as, else mine); falls back to
    // `displayOrder` when no custom order exists.
    return applyUserOrder(
      visibleNavCompanies(_companies.value, _selectedCompanyId.value, _selectedLeadAgencyId.value),
      effectiveCompanyOrder.value,
    )
  })

  const selectedCompany = computed(() => {
    if (_selectedCompanyId.value == null) return null
    const target = String(_selectedCompanyId.value)

    return _companies.value.find(c => String(c.id) === target) ?? null
  })

  const leadAgencies = computed(() => selectedCompany.value?.leadAgencies ?? [])

  const selectedLeadAgency = computed(() => {
    if (_selectedLeadAgencyId.value == null) return null
    const target = String(_selectedLeadAgencyId.value)

    return leadAgencies.value.find(la => String(la.id) === target) ?? null
  })

  /**
   * Fetch the nav payload. `currentCaseIds` is the slice the case list
   * is currently showing; passing it lets the server return the
   * per-case unread counts in the same round-trip (used by the case
   * list badges). Pass `null` (or omit) when you only need the nav
   * tree.
   */
  async function fetchNavData(currentCaseIds = null) {
    return _coreFetch(currentCaseIds)
  }

  /**
   * URL ↔ state reconciliation. Reads `cid`, `aid`, `caid` from the
   * current route, falls through to localStorage memory, and finally
   * defaults to the first visible company / first LA. Always one-way:
   * URL → state. After reconcile we push the resolved state back to
   * the URL via `syncToUrl` (cheap — only `router.replace` if
   * something actually changed).
   */
  function reconcileFromUrl() {
    const { cid, aid, caid } = route.query

    navTrace('reconcileFromUrl: reading URL → state', {
      cid: cid ?? null,
      aid: aid ?? null,
      caid: caid ?? null,
      companiesLoaded: _companies.value.length,
    })

    // Company: URL → last-used (memory) → first visible → first overall
    // (utils/caseNavigation).
    const { company, via: companyVia, urlMissed } = resolveNavCompany({
      companies: _companies.value,
      visible: visibleCompanies.value,
      urlCompanyId: cid,
      rememberedCompanyId: cid && _companies.value.some(c => String(c.id) === String(cid)) ? null : viewMemory.recallCompany(),
    })

    if (urlMissed) {
      navTraceFail('URL cid is NOT in the loaded company list — falling back', {
        cid: String(cid),
        knownIds: _companies.value.map(c => String(c.id)),
      })
    }
    _selectedCompanyId.value = company?.id != null ? String(company.id) : null
    if (_selectedCompanyId.value) viewMemory.rememberCompany(_selectedCompanyId.value)

    navTrace('reconcile: company resolved', {
      companyId: _selectedCompanyId.value,
      via: companyVia,
    })

    // Lead agency: URL `aid` → remembered LA → first — but only among LAs that
    // have cases (the bar hides empty ones, so selection lands on a visible
    // one; a remembered LA that has since emptied is skipped). Exception: an
    // explicit "All" pick (flag, no `aid` in the URL) stays null instead of
    // falling back to the first LA — so clicking All doesn't bounce back.
    const resolvedLa = resolveNavLeadAgency({
      company,
      urlLeadAgencyId: aid,
      isAll: _leadAgencyIsAll.value,
      rememberedLeadAgencyId: company && aid == null && !_leadAgencyIsAll.value
        ? viewMemory.recallLeadAgency(company.id)
        : null,
    })

    const laId = resolvedLa.leadAgencyId

    _leadAgencyIsAll.value = resolvedLa.isAll
    _selectedLeadAgencyId.value = laId
    if (_selectedCompanyId.value && _selectedLeadAgencyId.value) {
      viewMemory.rememberLeadAgency(_selectedCompanyId.value, _selectedLeadAgencyId.value)
    }

    if (aid != null && String(laId ?? '') !== String(aid)) {
      navTraceFail('URL aid did NOT survive (not among this company\'s LAs with cases)', {
        askedAid: String(aid),
        resolvedAid: laId != null ? String(laId) : null,
      })
    }

    // Case: URL only here. The remembered case is keyed by the planning day,
    // which the store resolves AFTER this — so the page's `resolveSelectedCase`
    // recalls it once the day + active cases are ready. No caid → leave null.
    _selectedCaseId.value = caid ? String(caid) : null

    navTrace('reconcile: done', {
      companyId: _selectedCompanyId.value,
      agencyId: _selectedLeadAgencyId.value,
      caseId: _selectedCaseId.value,
    })

    syncToUrl()
  }

  /**
   * Push the current selection back into the URL. Compares against
   * the existing query and only calls `router.replace` when something
   * changed, so we don't re-trigger any route watchers in the page.
   */
  function syncToUrl() {
    const cur = route.query

    const { next, changed } = nextNavQuery(cur, {
      companyId: _selectedCompanyId.value,
      leadAgencyId: _selectedLeadAgencyId.value,
      caseId: _selectedCaseId.value,
    })

    if (!changed) return

    navTrace('syncToUrl: REWRITING the address bar', {
      from: { cid: cur.cid ?? null, aid: cur.aid ?? null, caid: cur.caid ?? null },
      to: { cid: next.cid ?? null, aid: next.aid ?? null, caid: next.caid ?? null },
    })

    router.replace({ query: next })
  }

  function setCompany(company) {
    const id = company?.id != null ? String(company.id) : null

    if (id === String(_selectedCompanyId.value ?? '')) return

    _selectedCompanyId.value = id
    if (id) viewMemory.rememberCompany(id)

    // A fresh company starts on a specific LA (not "All").
    _leadAgencyIsAll.value = false

    // Resolve the LA for the new company: the remembered one if it still has
    // cases, else the first LA with cases (the bar hides empty ones). Keeps
    // "switch back to a company → land on the LA you were last on" working,
    // unless that LA has since emptied.
    const next = _companies.value.find(c => String(c.id) === id)
    const targetLaId = pickLeadAgencyId(next, viewMemory.recallLeadAgency(id))

    _selectedLeadAgencyId.value = targetLaId
    if (id && _selectedLeadAgencyId.value) {
      viewMemory.rememberLeadAgency(id, _selectedLeadAgencyId.value)
    }

    // Case is resolved by the page (per planning day) after the reload.
    _selectedCaseId.value = null
    syncToUrl()
  }

  function setLeadAgency(la) {
    const id = la?.id != null ? String(la.id) : null

    // `la == null` = the explicit "All lead agencies" pick. No-op if we're
    // already there; otherwise flip into All mode (LA null, but on purpose).
    if (id == null) {
      if (_leadAgencyIsAll.value && _selectedLeadAgencyId.value == null) return
      _leadAgencyIsAll.value = true
      _selectedLeadAgencyId.value = null
      _selectedCaseId.value = null
      syncToUrl()

      return
    }

    if (id === String(_selectedLeadAgencyId.value ?? '') && !_leadAgencyIsAll.value) return

    _leadAgencyIsAll.value = false
    _selectedLeadAgencyId.value = id
    viewMemory.rememberLeadAgency(_selectedCompanyId.value, id)

    // Case is resolved by the page (per planning day) after the reload.
    _selectedCaseId.value = null
    syncToUrl()
  }

  /**
   * Explicit user pick. The per-(company, LA, day) case memory is written by
   * the page (`onCaseChange`), which has the resolved planning day; here we
   * just update the id + URL. Alias of `selectCase` — kept so call sites read
   * intent (user pick vs system fallback).
   */
  function setCase(target) {
    selectCase(target)
  }

  /**
   * Put the scope EXACTLY where a deep-linked case lives (Sandy 2026-08-18).
   *
   * `setCompany` / `setLeadAgency` are the USER's doors: they apply the bar's
   * own rules — remember the last agency, skip agencies with no cases, treat a
   * null agency as an explicit "All". Every one of those is right for a click
   * and wrong for an arrival, where the server has already said which company
   * and which agency this case is in. Applied through them, a case sitting in
   * an agency the bar considers empty would land the page somewhere else.
   *
   * So this writes the ids straight through, and still records them as the
   * remembered scope — you did end up there, and coming back should return.
   *
   * @param {{companyId: string|null, agencyId: string|null}} target
   */
  function applyDeepLinkScope({ companyId = null, agencyId = null, caseId = null } = {}) {
    const cid = companyId != null ? String(companyId) : null

    _selectedCompanyId.value = cid
    if (cid) viewMemory.rememberCompany(cid)

    // No agency on the case = it belongs to the company as a whole, which the
    // bar shows as "All" — a real state, not a missing value.
    const aid = agencyId != null ? String(agencyId) : null

    _leadAgencyIsAll.value = aid == null
    _selectedLeadAgencyId.value = aid
    if (cid && aid) viewMemory.rememberLeadAgency(cid, aid)

    /*
     * THE CASE MOVES WITH ITS SCOPE (caught by nav-trace 2026-08-31). An
     * in-page arrival applies the target's scope while `_selectedCaseId`
     * still holds the PREVIOUS selection — and the `syncToUrl` below then
     * wrote that stale caid over the freshly-arrived one. The query watcher
     * saw its own echo come back as a brand-new deep-link to the OLD case,
     * captured it, and the click's real target was stomped (asked for
     * 10320, URL rewound to 10349). Callers on an arrival pass the caid
     * they're honouring; omitted (null) keeps the old behaviour for the
     * widening pass, where the caid in the URL is already the target's.
     */
    if (caseId != null) _selectedCaseId.value = String(caseId)

    syncToUrl()
  }

  /**
   * Internal selection — same as `setCase` but does NOT touch memory.
   * Used by `resolveSelectedCase` when it has to pick a fallback (the
   * first case in the list) because the saved one isn't currently in
   * the visible set. Without this split, the fallback would silently
   * overwrite the user's last explicit pick, making the memory feel
   * like it "doesn't stick" when they switch context and come back.
   */
  function selectCase(target) {
    const id = target?.id != null ? String(target.id) : null

    if (id === String(_selectedCaseId.value ?? '')) return

    _selectedCaseId.value = id
    syncToUrl()
  }

  /**
   * Page-mount entry point. Forces a fresh fetch of BOTH the nav
   * payload and the user preferences before letting the navbar
   * render. The composable lives at module scope so on a page re-
   * entry (or an account switch without a hard reload) `_companies`
   * still holds the previous user's data and `_hasInitialized` is
   * already true — without resetting both gates here, the chip list
   * would paint with the stale data, then flash to the new data when
   * the background fetch lands.
   *
   * Concretely:
   *   - Drop `_hasInitialized` so `ready` flips back to false and the
   *     navbar shows its skeleton while we refresh.
   *   - Refetch nav data + user prefs in parallel.
   *   - Reconcile state from the URL once both have landed.
   */
  async function initFromUrl() {
    _hasInitialized.value = false

    await Promise.all([
      fetchNavData(),
      prefs.loadAll(),
    ])

    _hasInitialized.value = true
    reconcileFromUrl()
  }

  function caseUnread(caseId) {
    if (caseId == null) return null

    return _caseUnreadById.value.get(String(caseId)) ?? null
  }

  /**
   * Local override for a case's unread count — used after the user
   * marks a follow-up read/unread so the badge updates immediately
   * without waiting for the next poll.
   */
  function patchCaseUnread(caseId, count) {
    if (caseId == null) return
    const next = new Map(_caseUnreadById.value)

    next.set(String(caseId), Math.max(0, Number(count) || 0))
    _caseUnreadById.value = next
  }

  return {
    // raw companies (used by Show empty switch)
    allCompanies: _companies,
    companies: visibleCompanies,
    leadAgencies,

    selectedCompany,
    selectedLeadAgency,
    selectedCompanyId: _selectedCompanyId,
    selectedLeadAgencyId: _selectedLeadAgencyId,
    selectedCaseId: _selectedCaseId,

    loading: _loading,
    ready,

    fetchNavData,
    initFromUrl,
    reconcileFromUrl,
    setCompany,
    setLeadAgency,
    setCase,
    selectCase,
    applyDeepLinkScope,
    caseUnread,
    patchCaseUnread,

    // Phase 4: polling + invalidation. Same functions as the standalone
    // exports below — exposed here so consumers using the composable
    // don't need a second import.
    start: startCaseNavPolling,
    stop: stopCaseNavPolling,
    invalidate: invalidateCaseNav,
  }
}
